import { doc, getDoc, setDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logAdminActivity } from '../firebase/helpers';
import { verifyAdminAccess } from './adminService';

const WHATSAPP_CONFIG_COLLECTION = 'whatsappConfig';
const WHATSAPP_CONFIG_DOC_ID = 'customerService';

/**
 * Get WhatsApp configuration for customer services
 */
export const getWhatsAppConfig = async () => {
  try {
    const configRef = doc(db, WHATSAPP_CONFIG_COLLECTION, WHATSAPP_CONFIG_DOC_ID);
    const configSnap = await getDoc(configRef);
    
    if (configSnap.exists()) {
      return configSnap.data();
    }
    
    // Return default configuration if not found
    return {
      services: [
        {
          id: '1',
          label: 'Customer Service 1',
          phoneNumber: '+14083335900', // Pre-set USA number
          message: 'Hello, I need assistance with your service'
        },
        {
          id: '2',
          label: 'Customer Service 2',
          phoneNumber: '',
          message: 'Hello, I need assistance with your service'
        },
        {
          id: '3',
          label: 'Customer Service 3',
          phoneNumber: '',
          message: 'Hello, I need assistance with your service'
        }
      ],
      defaultMessage: 'Hello, I need assistance with your service',
      serviceName: 'Circana',
      updatedAt: null
    };
  } catch (error) {
    console.error('Error getting WhatsApp config:', error);
    throw error;
  }
};

/**
 * Update WhatsApp configuration (Admin only)
 */
export const updateWhatsAppConfig = async (config) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    // Validate configuration
    if (!config.services || !Array.isArray(config.services)) {
      throw new Error('Invalid configuration: services array required');
    }
    
    if (config.services.length !== 3) {
      throw new Error('Invalid configuration: exactly 3 services required');
    }
    
    // Validate each service
    for (const service of config.services) {
      if (!service.id || !service.label) {
        throw new Error('Invalid service: id and label required');
      }
      
      if (service.phoneNumber) {
        const cleaned = service.phoneNumber.replace(/\D/g, '');
        if (cleaned.length < 10) {
          throw new Error(`Invalid phone number for ${service.label}: must be at least 10 digits`);
        }
      }
    }
    
    // Prepare update data
    const updateData = {
      services: config.services,
      defaultMessage: config.defaultMessage || 'Hello, I need assistance with your service',
      serviceName: config.serviceName || 'Circana',
      updatedAt: Timestamp.now(),
      updatedBy: adminInfo.adminId,
      updatedByEmail: adminInfo.adminEmail
    };
    
    // Save to database
    const configRef = doc(db, WHATSAPP_CONFIG_COLLECTION, WHATSAPP_CONFIG_DOC_ID);
    await setDoc(configRef, updateData, { merge: true });
    
    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'updateWhatsAppConfig',
      changes: {
        services: config.services.map(s => ({
          id: s.id,
          label: s.label,
          phoneNumber: s.phoneNumber ? s.phoneNumber.replace(/\d(?=\d{4})/g, '*') : '' // Mask phone numbers in log
        })),
        defaultMessage: config.defaultMessage,
        serviceName: config.serviceName
      },
      reason: 'Updated WhatsApp customer service configuration',
      ipAddress: null
    });
    
    return {
      success: true,
      message: 'WhatsApp configuration updated successfully'
    };
  } catch (error) {
    console.error('Error updating WhatsApp config:', error);
    throw error;
  }
};

/**
 * Format phone number for WhatsApp URL (remove + sign and spaces)
 * This is used only for generating the WhatsApp URL
 */
export const formatPhoneNumberForUrl = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove + sign, spaces, dashes, and other non-digit characters
  let cleaned = phoneNumber.replace(/[\s\-\(\)\+]/g, '');
  
  // Remove all non-digit characters
  cleaned = cleaned.replace(/\D/g, '');
  
  return cleaned;
};

/**
 * Format phone number for display (preserve + sign)
 * This is used for displaying in input fields
 */
export const formatPhoneNumberForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove spaces and dashes but keep + sign
  let cleaned = phoneNumber.replace(/[\s\-\(\)]/g, '');
  
  // Ensure it starts with + if it has digits
  if (cleaned && !cleaned.startsWith('+') && /^\d/.test(cleaned)) {
    cleaned = '+' + cleaned;
  }
  
  return cleaned;
};

/**
 * Generate WhatsApp URL
 * Note: WhatsApp URL format requires number WITHOUT + sign
 */
export const generateWhatsAppUrl = (phoneNumber, message) => {
  const formattedNumber = formatPhoneNumberForUrl(phoneNumber);
  if (!formattedNumber || formattedNumber.length < 10) {
    throw new Error('Invalid phone number');
  }
  
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedNumber}?text=${encodedMessage}`;
};

/**
 * Validate phone number format
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return { valid: false, error: 'Phone number is required' };
  }
  
  // Remove + sign, spaces, dashes for validation
  let cleaned = phoneNumber.replace(/[\s\-\(\)\+]/g, '');
  cleaned = cleaned.replace(/\D/g, '');
  
  if (cleaned.length < 10) {
    return { valid: false, error: 'Phone number must be at least 10 digits' };
  }
  
  if (cleaned.length > 15) {
    return { valid: false, error: 'Phone number cannot exceed 15 digits' };
  }
  
  // Validate country code (should start with 1-9, not 0)
  if (cleaned.length > 0 && cleaned[0] === '0') {
    return { valid: false, error: 'Phone number should not start with 0. Use country code format (e.g., +1 for USA)' };
  }
  
  return { valid: true, error: null };
};

/**
 * Get country codes for dropdown
 */
export const getCountryCodes = () => {
  return [
    { code: '+1', country: 'United States (USA)', flag: '🇺🇸' },
    { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
    { code: '+44', country: 'United Kingdom (UK)', flag: '🇬🇧' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { code: '+971', country: 'United Arab Emirates (UAE)', flag: '🇦🇪' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
    { code: '+63', country: 'Philippines', flag: '🇵🇭' }
  ];
};

/**
 * Subscribe to WhatsApp config changes (real-time updates)
 */
export const subscribeToWhatsAppConfig = (callback) => {
  const configRef = doc(db, WHATSAPP_CONFIG_COLLECTION, WHATSAPP_CONFIG_DOC_ID);
  
  return onSnapshot(
    configRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data());
      } else {
        // Return default if not found
        callback({
          services: [
            {
              id: '1',
              label: 'Customer Service 1',
              phoneNumber: '+14083335900',
              message: 'Hello, I need assistance with your service'
            },
            {
              id: '2',
              label: 'Customer Service 2',
              phoneNumber: '',
              message: 'Hello, I need assistance with your service'
            },
            {
              id: '3',
              label: 'Customer Service 3',
              phoneNumber: '',
              message: 'Hello, I need assistance with your service'
            }
          ],
          defaultMessage: 'Hello, I need assistance with your service',
          serviceName: 'Circana',
          updatedAt: null
        });
      }
    },
    (error) => {
      console.error('Error subscribing to WhatsApp config:', error);
      callback(null);
    }
  );
};

